/**
 * Backups: AI Workbench backups, encrypted on the machine before they leave it (encrypted
 * backups v2, packages/shared/src/schemas/backupV2.ts). A person sees their own by device and
 * date, what they take, how long they are kept, and who can unlock them; restoring happens on
 * the workbench, which holds the keys. Administrators see a summary per person (never content
 * or keys) and the organisation recovery key, which owners set up from a workbench.
 *
 * The data column (Layout.tsx): a header (Yours or Organisation for an administrator), a How it
 * works on restoring, the numbers, then list groups of rows, each with at most one control.
 */

import type { BackupAdminOverview, BackupPersonSummary, BackupRepositoryAnswer, BackupRecipient, BackupRetention, BackupSnapshotSummary, OrganisationRecoveryKey } from '@aioe/shared';
import { dateLong, dateTime } from '@aioe/shared/wording/dateTime';
import { Clock, ClockArrowRotateLeft, Display, HardDrive, Key, Persons, ShieldCheck } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { Fragment, useMemo, useState } from 'react';

import { ApiError, problemText, routes } from '../api/client.ts';
import { isCloud } from '../edition.ts';
import { ChoiceSelect, ConfirmDialog, Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, Toggle } from '../components/ui.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { MyWorkbenchBackup, WorkbenchBackupsAdmin } from '../components/workbenchBackups.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone = VIEW_TONES.backups!;

export function bytes(n: number | undefined): string {
  if (n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
const when = (iso: string) => dateTime(iso, { date: 'medium', year: false, weekday: true });
const day = (iso: string) => dateLong(iso);
const REASON: Record<BackupSnapshotSummary['reason'], string> = { scheduled: 'On schedule', manual: 'Backed up by hand', 'before-restore': 'Taken before a restore' };
const DELETED_BY: Record<NonNullable<BackupSnapshotSummary['deletedBy']>, string> = { owner: 'Deleted by you', administrator: 'Deleted by an administrator', retention: 'Past its keeping time' };
const KIND: Record<BackupRecipient['kind'], { text: string }> = {
  device: { text: 'A device of yours' },
  recovery: { text: 'Your Emergency Kit' },
  organisation: { text: 'The organisation recovery key' },
  escrow: { text: 'The platform’s recovery service' },
};

/** How long backups are kept, as a headline and a line of detail. */
function keeping(r: BackupRetention): { value: string; detail: string } {
  const parts = [r.hourly && `${r.hourly} hourly`, r.daily && `${r.daily} daily`, r.weekly && `${r.weekly} weekly`, r.monthly && `${r.monthly} monthly`].filter(Boolean) as string[];
  if (parts.length === 0) return { value: `${r.withinDays} days`, detail: `Every backup from the last ${r.withinDays} days` };
  const value = r.monthly ? `${r.monthly} months` : r.weekly ? `${r.weekly} weeks` : r.daily ? `${r.daily} days` : `${r.hourly} hours`;
  return { value, detail: parts.join(' · ') };
}

/** A step's number in a row's icon well. */
const stepNumber = (n: number) => <span className="text-xs font-semibold tabular-nums">{n}</span>;

export function BackupsPage() {
  // Backups switched off answer 503 (`backups_off`): that is a state of the page, not an error, so it loads as null.
  const repo = useLoad(() => routes.backupRepository().catch((e: unknown) => (e instanceof ApiError && e.status === 503 ? null : Promise.reject(e))), []);
  // One person is enough to know this is an administrator and to read the organisation's totals; the people list pages itself.
  const overview = useLoad(() => routes.backupsOverview({ limit: 1 }).catch(() => undefined), []);
  const [view, setView] = useState<'mine' | 'organisation'>('mine');
  const off = repo.data === null || (repo.error !== undefined && /\b503\b/.test(repo.error));
  const admin = overview.data !== undefined;

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<ClockArrowRotateLeft />}
        title="Backups"
        subtitle="Encrypted backups the organisation keeps but cannot read, and the restore point that sets up a new desktop."
        actions={
          admin && !off ? (
            <Segment aria-label="Whose backups" size="sm" selectedKey={view} onSelectionChange={(k) => setView(k === 'organisation' ? 'organisation' : 'mine')} className="w-fit">
              <Segment.Item id="mine">Yours</Segment.Item>
              <Segment.Item id="organisation">Organisation</Segment.Item>
            </Segment>
          ) : undefined
        }
      />
      <Explainer id="backups" tone={tone} icon={<ClockArrowRotateLeft />} title="How backups and restoring work" summary="Encrypted on your machine first; restored by AI Workbench, never here">
        <p>Each backup is encrypted on your machine before it leaves it, so the organisation keeps it but cannot read it. Only the devices and keys listed under Who can unlock them can open it.</p>
        <p>Restoring happens on AI Workbench, never in this console: open it on the machine to restore, sign in to this organisation, then choose Backups and Restore in Settings. On a new machine, unlock with your Emergency Kit, or ask an owner to recover it with the organisation key.</p>
        <p>The workbench restores into a separate folder first, backs up what was there, then swaps the restored copy in. Sign-ins and paired phones are set up again: keys and tokens are never in a backup.</p>
      </Explainer>
      <PartBoundary key="backups" name="Backups" tone={tone}>
        {off ? (
          <>
            <Empty
              tone={tone}
              icon={<ClockArrowRotateLeft />}
              title="Encrypted backups are not switched on here"
              body={`${isCloud() ? 'Backups are not available for your organisation yet' : 'This platform has no storage set up for backups yet. Whoever runs it can switch them on'}; until then AI Workbench keeps its backups on your own computer.`}
            />
            <MyWorkbenchBackup />
            <WorkbenchBackupsAdmin />
          </>
        ) : view === 'organisation' && overview.data ? (
          <>
            <OrganisationBackups overview={overview.data} reload={overview.reload} />
            <WorkbenchBackupsAdmin />
          </>
        ) : (
          <>
            <MyBackups repo={{ data: repo.data ?? undefined, error: repo.error, reload: repo.reload }} />
            <MyWorkbenchBackup />
          </>
        )}
      </PartBoundary>
    </PageColumn>
  );
}

interface PendingAction {
  title: string;
  body: string;
  label: string;
  run: () => Promise<unknown>;
}

/** The confirmation for whichever destructive action is waiting; cleared before the action runs. */
function PendingConfirm({ pending, onClose }: { pending: PendingAction | undefined; onClose: () => void }) {
  return (
    <ConfirmDialog
      isOpen={!!pending}
      title={pending?.title ?? ''}
      confirmLabel={pending?.label ?? 'Delete'}
      danger
      onCancel={onClose}
      onConfirm={() => {
        const p = pending;
        onClose();
        return p?.run();
      }}
    >
      {pending?.body}
    </ConfirmDialog>
  );
}

function MyBackups({ repo }: { repo: { data?: BackupRepositoryAnswer; error?: string; reload: () => void } }) {
  const snapshots = useLoad(() => routes.backupSnapshots(), []);
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<PendingAction>();
  const act = async (f: () => Promise<unknown>, after: () => void) => {
    setError(undefined);
    try {
      await f();
      after();
    } catch (e) {
      setError(problemText(e));
    }
  };
  const all = snapshots.data ?? [];
  const live = all.filter((s) => !s.deletedAt);
  const deleted = all.filter((s) => s.deletedAt);
  const byDevice = useMemo(() => {
    const m = new Map<string, BackupSnapshotSummary[]>();
    for (const s of live) m.set(s.deviceId, [...(m.get(s.deviceId) ?? []), s]);
    return [...m.entries()];
  }, [live]);
  const r = repo.data;
  const keep = r ? keeping(r.retention) : undefined;
  const newest = live[0];
  const recipients = r?.repository?.recipients ?? [];

  return (
    <>
      <div className="art-stats">
        <Stat tone={tone} icon={<ClockArrowRotateLeft />} label="Backups" value={snapshots.data ? live.length : undefined} detail={snapshots.data ? `From ${byDevice.length} ${byDevice.length === 1 ? 'device' : 'devices'}` : ''} />
        <Stat tone={tone} icon={<HardDrive />} label="Space used" value={r ? bytes(r.quota.usedBytes) : undefined} detail={r ? (r.quota.limitBytes ? `Of ${bytes(r.quota.limitBytes)} in this organisation` : 'No limit in this organisation') : ''} />
        <Stat tone={tone} icon={<Display />} label="Last backup" value={newest ? timeAgo(newest.committedAt) : snapshots.data ? 'Never' : undefined} detail={newest ? `From ${newest.deviceName ?? 'a desktop'}` : ''} />
        <Stat tone={tone} icon={<Clock />} label="Kept for" value={keep?.value} detail={keep?.detail ?? ''} />
      </div>
      <ErrorNote error={error ?? (repo.error && !/\b503\b/.test(repo.error) ? repo.error : undefined) ?? snapshots.error} />
      <PendingConfirm pending={pending} onClose={() => setPending(undefined)} />

      {snapshots.data ? (
        <ListGroup
          title="Your backups"
          count={live.length}
          description={live.length ? 'Newest first, by device.' : undefined}
          empty={
            live.length === 0 ? (
              <Empty tone={tone} icon={<ClockArrowRotateLeft />} title="Nothing backed up yet" body="Turn on Backups in AI Workbench’s Settings while you are signed in here. Each backup is encrypted on your machine first, so only your devices can open it." />
            ) : undefined
          }
        >
          {byDevice.map(([deviceId, list]) => (
            <Fragment key={deviceId}>
              <ListBlock className="flex items-center gap-2 py-2 text-xs font-medium text-muted">
                <Display className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{list[0]!.deviceName ?? deviceId}</span>
                <span className="tabular-nums">· {list.length}</span>
              </ListBlock>
              {list.map((s) => (
                <ListRow
                  key={s.id}
                  title={when(s.committedAt)}
                  description={`${REASON[s.reason]} · ${bytes(s.storedBytes)}, ${bytes(s.newBytes)} new · AI Workbench ${s.appVersion}`}
                  compactControl
                  control={
                    <RowMenu
                      label={`More for the backup from ${when(s.committedAt)}`}
                      items={[{ id: 'delete', label: 'Delete', danger: true }]}
                      onAction={() => setPending({ title: `Delete the backup from ${when(s.committedAt)}`, body: 'It is kept for 30 days in case you change your mind.', label: 'Delete', run: () => act(() => routes.deleteBackupSnapshot(s.id), snapshots.reload) })}
                    />
                  }
                />
              ))}
            </Fragment>
          ))}
        </ListGroup>
      ) : null}

      {deleted.length > 0 && (
        <ListGroup title="Deleted" count={deleted.length} description="Kept for 30 days, then removed for good.">
          {deleted.map((s) => (
            <ListRow
              key={s.id}
              title={`${when(s.committedAt)} · ${s.deviceName ?? s.deviceId}`}
              description={`${DELETED_BY[s.deletedBy ?? 'owner']} · removed for good on ${day(s.purgeAfter ?? s.deletedAt!)}`}
              compactControl
              control={
                <Button size="sm" variant="ghost" onPress={() => void act(() => routes.undeleteBackupSnapshot(s.id), snapshots.reload)}>
                  Undelete
                </Button>
              }
            />
          ))}
        </ListGroup>
      )}

      {r?.repository && (
        <ListGroup title="Who can unlock them" count={recipients.length} description="Each holds its own locked copy of your backup key.">
          {recipients.map((x) => {
            const required = x.kind === 'organisation' && r.organisation?.required && r.organisation.recipientId === x.recipientId;
            const removable = x.kind === 'device' && recipients.length > 1 && !required;
            const name = x.label ?? 'this device';
            return (
              <ListRow
                key={x.recipientId}
                title={x.label ?? KIND[x.kind].text}
                description={`${x.label ? `${KIND[x.kind].text} · ` : ''}added ${day(x.addedAt)}${required ? ' · required by your organisation' : ''}`}
                compactControl
                control={
                  removable ? (
                    <RowMenu
                      label={`More for ${name}`}
                      items={[{ id: 'stop', label: 'Stop it unlocking your backups', danger: true }]}
                      onAction={() => setPending({ title: `Stop ${name} unlocking your backups`, body: 'Do this for a device you no longer use.', label: 'Stop', run: () => act(() => routes.removeBackupRecipient(x.recipientId, r.repository!.revision), repo.reload) })}
                    />
                  ) : undefined
                }
              />
            );
          })}
        </ListGroup>
      )}
    </>
  );
}

function OrganisationBackups({ overview: o, reload }: { overview: BackupAdminOverview; reload: () => void }) {
  const key = useLoad(() => routes.backupRecoveryKey(), []);
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<PendingAction>();
  const act = async (f: () => Promise<unknown>, after: () => void) => {
    setError(undefined);
    try {
      await f();
      after();
    } catch (e) {
      setError(e instanceof ApiError ? problemText(e) : String(e));
    }
  };
  const t = o.totals ?? { people: o.people.length, withBackups: o.people.filter((p) => p.snapshots > 0).length, snapshots: o.people.reduce((n, p) => n + p.snapshots, 0), usedBytes: o.people.reduce((n, p) => n + p.usedBytes, 0), covered: o.people.filter((p) => p.hasOrganisationRecipient).length, stale: 0, overQuota: 0 };
  const keep = keeping(o.retention);
  // The people are searched and paged on the server; the totals above count everyone.
  const search = useListSearch();
  const [show, setShow] = useState<'all' | 'stale' | 'over'>('all');
  const query = { q: search.q, show };
  const people = usePagedList((cursor) => routes.backupPeoplePage({ ...query, cursor }), query, { keyOf: (p) => p.userId });
  const narrowed = Boolean(search.q) || show !== 'all';
  const refresh = () => (reload(), people.reload());

  return (
    <>
      <div className="art-stats">
        <Stat tone={tone} icon={<Persons />} label="People backing up" value={t.withBackups} detail={`${t.snapshots.toLocaleString()} backups kept`} />
        <Stat tone={tone} icon={<HardDrive />} label="Space used" value={bytes(t.usedBytes)} detail={o.quotaPerPerson ? `Up to ${bytes(o.quotaPerPerson)} each` : 'No limit per person'} />
        <Stat tone={tone} icon={<Clock />} label="Kept for" value={keep.value} detail={keep.detail} />
        <Stat tone={tone} icon={<ShieldCheck />} label="Recovery key" value={o.recoveryKey.set ? 'Set up' : 'Not set up'} detail={o.recoveryKey.set ? `${t.covered} of ${t.people} can be recovered` : 'Owners set it up on a workbench'} />
      </div>
      <ErrorNote error={error ?? key.error} />
      <PendingConfirm pending={pending} onClose={() => setPending(undefined)} />

      <ListGroup
        title="People"
        count={people.loaded ? listCount(people.items.length, { one: 'person', many: 'people' }, { total: narrowed ? undefined : t.people, hasMore: people.hasMore, searched: narrowed }) : undefined}
        description="Summaries only: nobody here can open a backup."
        actions={
          <>
            <SearchBox label="Search people" value={search.typed} onChange={search.setTyped} placeholder="Search people" className="w-40 max-w-full @lg:w-56" />
            <ChoiceSelect
              label="Show"
              value={show}
              options={{ all: 'Everyone', stale: `No backup in 7 days${t.stale ? ` (${t.stale})` : ''}`, over: `Over quota${t.overQuota ? ` (${t.overQuota})` : ''}` }}
              onChange={setShow}
              className="w-fit min-w-40 max-w-full"
            />
          </>
        }
        empty={
          people.loaded && people.items.length === 0 ? (
            narrowed ? (
              <NoMatch tone={tone} icon={<Persons />} q={search.q} filtered={show !== 'all'} noun="people" onClear={() => (search.clear(), setShow('all'))} />
            ) : (
              <Empty tone={tone} icon={<Persons />} title="No one has backed up yet" body="People appear here once their AI Workbench makes its first backup to this organisation." />
            )
          ) : undefined
        }
      >
        {people.items.map((p) => (
          <PersonRow key={p.userId} person={p} recoveryKeySet={o.recoveryKey.set} onDelete={() => setPending({ title: `Delete every backup ${p.name ?? p.email ?? 'this person'} has here`, body: 'They are kept for 30 days, then removed for good.', label: 'Delete', run: () => act(() => routes.deletePersonBackups(p.userId), refresh) })} />
        ))}
        {people.hasMore ? <ShowMore onPress={people.showMore} pending={people.loadingMore} /> : null}
      </ListGroup>

      <RecoveryKeyGroup recoveryKey={key.data} loaded={key.data !== undefined || key.error !== undefined} onRequire={(required) => void act(() => routes.requireBackupRecoveryKey(required), () => (key.reload(), reload()))} />
    </>
  );
}

function PersonRow({ person: p, recoveryKeySet, onDelete }: { person: BackupPersonSummary; recoveryKeySet: boolean; onDelete: () => void }) {
  const who = p.name ?? p.email ?? p.userId;
  return (
    <ListRow
      title={who}
      description={
        <>
          {p.snapshots === 0
            ? 'Set up, nothing backed up yet'
            : `${p.snapshots} ${p.snapshots === 1 ? 'backup' : 'backups'} from ${p.devices} ${p.devices === 1 ? 'device' : 'devices'} · ${bytes(p.usedBytes)}${p.lastBackupAt ? ` · last ${timeAgo(p.lastBackupAt)}` : ''}`}
          {/* Said only where it is missing: a word on every row would be noise. */}
          {recoveryKeySet && !p.hasOrganisationRecipient ? ' · not yet recoverable with the organisation key' : ''}
        </>
      }
      compactControl
      control={p.snapshots > 0 ? <RowMenu label={`More for ${who}`} items={[{ id: 'delete', label: 'Delete their backups', danger: true }]} onAction={onDelete} /> : undefined}
    />
  );
}

function RecoveryKeyGroup({ recoveryKey: k, loaded, onRequire }: { recoveryKey: OrganisationRecoveryKey | null | undefined; loaded: boolean; onRequire: (required: boolean) => void }) {
  if (!loaded) return null;
  if (!k) {
    return (
      <ListGroup
        title="Organisation recovery key"
        description={`If someone loses every device and their Emergency Kit, their backups cannot be opened by anyone, ${isCloud() ? 'AI Cloud' : 'this platform'} included. An organisation recovery key is a second way in that its owners hold, the way a business keeps a master key.`}
      >
        <ListRow icon={stepNumber(1)} title="An owner sets it up on AI Workbench" description="In Settings, under Organisation, choose Backup recovery. The workbench makes the key on that machine and gives this organisation only the half that locks things." />
        <ListRow icon={stepNumber(2)} title="Each owner holds a copy" description={`The half that unlocks is sealed separately for each owner’s own device. ${isCloud() ? 'AI Cloud keeps' : 'This platform keeps'} those sealed copies and cannot open them.`} />
        <ListRow icon={stepNumber(3)} title="Workbenches add it to their backups" description="At their next backup, members’ workbenches lock a copy of their backup key to it. You can then require it for everyone here." />
      </ListGroup>
    );
  }
  return (
    <ListGroup title="Organisation recovery key" count={k.holders.length || undefined} description={`Set up ${day(k.createdAt)}.`}>
      <ListRow icon={<ShieldCheck />} title="Required for everyone" description="When on, a workbench must add this key to its backups before it can back up again." control={<Toggle checked={k.required} onChange={onRequire} label="Require the organisation recovery key" />} />
      {k.holders.map((h) => (
        <ListRow key={`${h.userId}/${h.keyId}`} icon={<Key />} title={h.name ?? h.userId} description={`Holds a sealed copy on ${h.keyId.replace(/^device:/, '')}`} />
      ))}
    </ListGroup>
  );
}
